import Backbone from 'backbone'
import template from './field-form.hbs'
import $ from 'jquery'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import selects from 'scoreboard/components/selects'
import FormView from 'scoreboard/components/form-view'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator
	from 'scoreboard/components/validators/required-validator'
import formHelper from 'scoreboard/components/form-helper'
import calendars from 'scoreboard/components/calendars'
import dropdown from 'scoreboard/components/dropdown'
import Tooltip from 'scoreboard/components/tooltip'
import currency from 'scoreboard/components/currency'
import config from 'scoreboard/components/config'
import { adminTreesLink } from 'scoreboard/components/admin-link'
import dataTypes from 'scoreboard/datasets/edit/data-types'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import * as aggregationTypeOptions
	from 'scoreboard/datasets/aggregation-type-options'
import Dataset from 'scoreboard/datasets/model/dataset'
import DatasetField from 'scoreboard/datasets/model/dataset-field'
import EditableButton from 'scoreboard/components/editable-button'
import FieldFormEquationForm
	from 'scoreboard/datasets/edit/fields/field-form-equation-form'
import appDisplay from 'scoreboard/components/app-display'
import unwrapModel from 'scoreboard/components/unwrap-model'
import FieldEquationFormatter
	from 'scoreboard/datasets/edit/field-equation-formatter'
import LinkDatasetsConfirmPrompt
	from 'scoreboard/datasets/edit/link-datasets-confirm-prompt'
import ObjectSelectorButton from 'scoreboard/components/object-selector-button'
import * as datasetSelectorOptions
	from 'scoreboard/datasets/dataset-selector-options'
import { renderTimeZoneSelect } from 'scoreboard/components/schedule-picker'
import momentHelper from 'scoreboard/components/moment-helper'
import enums from 'scoreboard/components/enums'
import AnalyzeClusters from 'scoreboard/datasets/edit/fields/analyze-clusters'
import errorHandler from 'scoreboard/components/error-handler'
import SelectGeographicAncestor
	from 'scoreboard/datasets/edit/fields/geographic-ancestor-selector'
import getRequiredGeographicAncestorTypes
	from 'scoreboard/datasets/edit/fields/get-required-geographic-ancestor-types'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import DataTypeSelector
	from 'scoreboard/datasets/edit/fields/data-type-selector'
import dataSuperTypes, {
	dataSuperTypesByDataType,
	getDataSuperTypeForDataType
} from 'scoreboard/datasets/edit/fields/data-super-types'
import isGeographicOrRollup from '../../is-geographic-or-rollup'
import {
	disableCheckbox,
	unDisableCheckbox
} from 'scoreboard/components/disable-checkbox'
import constants from 'scoreboard/components/constants'
import disableControls from 'scoreboard/components/disable-controls'
import regexpValidatorFactory
	from '../../../components/validators/regexp-validator-factory'
import EditableFieldChecker from './editable-field-checker'

import DataType, {
	getValidDataTypesToChangeFrom,
	getValidDataTypesToChangeTo,
	hasValidTypeToChangeFrom,
	hasValidTypeToChangeTo,
	isCompatibleDataType
} from 'scoreboard/datasets/model/data-type'

const {DATASET_FIELD_TYPE} = constants
const {
	NUMBER,
	PERCENT,
	CURRENCY,
	YES_NO,
	DATE,
	TIME,
	DATE_TIME,
	ROLLUP,
	CALENDAR_PERIOD,
	DATASET_LINK,
	CLUSTER,
} = DataType

const maxClusterCount = 25
const pkRequiredForManualFieldErrorCode = 'datasets.fields.primaryKeyRequired.manualField'
const fieldNameValidator = regexpValidatorFactory(
	'^[^[\\]]*$', // allow any character except [ and ]
	'datasets.fields.error.label.disallowedCharacters'
)
const FieldForm = Backbone.View.extend({

	tagName: 'form',

	className: 'field-form',

	events: {
		'change [name=fieldType]': 'changeFieldType',
		'click .set-equation': 'renderEquationForm',
		'change [name=column]': 'changeColumn',
		'change [name=aggregationType]': 'changeAggregationType',
		'change [name=treeId]': 'updateKeySet',
		'change [name=timeZoneToggle]': 'onChangeTimeZoneToggle',
		'change [name=numClusters]': 'onChangeNumClusters',
		'change [name=clusteredFieldIds]': 'onChangeClusteredFieldIds',
		'click .cluster-analyze': 'onClickAnalyzeClusters',
		'click .save-without-clusters': 'onClickSaveWithoutClusters'
	},

	/** @type {Dataset} */
	model: null,
	/** @type {Dataset} */
	linkedDataset: null,
	/** validator for regex */

	/**
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Dataset} options.model the dataset currently being edited
	 *
	 * @param {DatasetField} options.field a plain object containing the field info. Note:
	 * this is not the field as it is saved in the DB, but as the user has entered it
	 *
	 * @param {DatasetField} options.originalField the field as it is saved in the DB
	 * There are scenarios like the one in #55984 where we want to use this rather than
	 * options.field to determine what to show in the form
	 *
	 * @param {Array} options.trees a list of the available dataset trees
	 *
	 * @param {Array} options.datasets a list of the root datasets accessible to this
	 * user
	 *
	 * @param {Number} options.primaryKey the primary key (needed b/c the value
	 * in the model may not reflect the most recent selections in the Datasets Edit
	 * UI)
	 */
	initialize: function(options) {
		this.options = options || {}
		this.dataType = options.field.dataType
		this.treesById = _.keyBy(this.options.trees, 'id')
		this.treeItemsById = _.keyBy(this.model.get('treeItems'), 'id')
		this.datasetsById = _.keyBy(this.options.datasets, 'id')
		const metadata = this.model.get('dataSourceMetadata')
		// note these keys are the column names, which can be different than DatasetField.label
		this.metadataFieldsByName = _.keyBy(metadata.fields, 'name')
		if (!this.model.isNew() && this.dataType === DATASET_LINK) {
			let linkedDatasetId = this.options.field.linkedDatasetId
			this.linkedDataset = this.model.datasetsById[linkedDatasetId]
		}
		this.fieldEquationFormatter = new FieldEquationFormatter({ dataset: this.model })
		this.field = _.cloneDeep(this.options.field)
		// this is the field as it is saved in the DB.
		this.originalField = _.cloneDeep(this.options.originalField)
		this.editableFieldChecker = new EditableFieldChecker({ dataset: this.model })
		this.fieldModel = new DatasetField(this.field)
		this.markedFields = []
		this.initialFieldType = this.field.fieldType
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		this.renderColumnDropdown()
		this.renderFieldType()
		// we have all the data we need to know if the formView will allowCleanSubmission
		this.initializeFormView()
		this.initializeAdjustTimesZones()
		this.renderEquationButton()
		this.renderNameInput()
		this.renderCalendarDropdown()
		this.renderRollupTreesDropdown()
		this.renderCurrencyCodeDropdown()
		this.renderDataTypeSelect()
		this.renderMissingValueTreatment()
		formHelper.writeToDom(this.field, this.el)
		this.renderNumClustersSelect()
		// after formHelper.writeToDom
		selects(this._unconfiguredSelects())
		// after the call to selects
		this.renderFieldGroupDropdown()
		this.renderLinkedDatasetSelector()
		this.renderTimeZoneSelect()
		this.renderTimeDisplayTypeSelect()
		this.renderClusteredFieldsSelect()

		this.renderSelectGeographicAncestor()

		this.changeDataType()
		this.toggleKeySet(this.field.dataType, this.field.treeId, this.field.keySetId)

		// #49744 move _maybeApplyLockedDataTypeState til after everything else
		// has rendered
		this._maybeApplyLockedDataTypeState()

		if (this.options.wizard) {
			this._renderWizard({
				formView: this.formView,
				stateSaver: this.saveState.bind(this)
			})
		}

		this.isEditable = this.fieldModel.get('editable') || false
		// Call changeFieldType() after everything else has been rendered so
		// enabled/disabled states on fields are properly set
		this.changeFieldType()

		this.formView.start()

		return this
	},

	/**
	 * @private
	 * Return the selects that are safe to be initialized without any special
	 * options.
	 */
	_unconfiguredSelects: function() {
		return this.$('select:not(.data-type, .data-super-type, .field-group, .clustered-field-ids, .exempted)')
	},

	/**
	 * IMPORTANT: this.options.wizard.render should never be invoked outside of
	 * this method because the "Save Without Reclustering" button may need to be
	 * [re-]added.
	 * @private
	 */
	_renderWizard: function(options) {
		this.options.wizard.render(options)

		// For checking if number of clusters selection has changed
		const selectedNumClusters = this.$('[name="numClusters"]').val() || undefined
		const modelNumClusters = this.field.autoCluster ? undefined :
			this.field.numClusters
		const sameNumClusters = selectedNumClusters === modelNumClusters

		// For checking if the clustering fields have changed
		const selectedClusterFieldIds =
			(this.$('[name="clusteredFieldIds"]').val() || []).map(it => parseInt(it))
		const modelClusterFieldIds = new Set(this.field.clusteredFieldIds)
		const sameClusterFieldIds =
			selectedClusterFieldIds.length === modelClusterFieldIds.size &&
				_.every(selectedClusterFieldIds, it => modelClusterFieldIds.has(it))

		const reclusteringNotRequired = sameNumClusters && sameClusterFieldIds
		if (this.field.fieldType === CLUSTER && reclusteringNotRequired &&
		this.isEditingField()) {
			const skipClusterLabel =
				i18n.get('datasets.field.clusterFields.edit.saveWithoutRecluster')
			const skipClusterButton =
				`<button type="button"
								 class="button button-default save-without-clusters">
					${skipClusterLabel}
				</button>`

			this.$('.button-container').append(skipClusterButton)
		}
	},

	initializeFormView: function() {
		// if the field has a label already, it's OK to go to the next step
		// right away
		const existingFieldLabel = !!_.get(this.options, 'field.label')
		// if the default is a column from the source data, its OK to go to the next step
		const basicColumn = !!(this.fieldTypeDropdown.val() === 'basic' &&
			this.columnDropdown.val())

		let FieldFormView = FormView.extend({
			test: () => this.test()
		})

		this.formView = new FieldFormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: existingFieldLabel || basicColumn,
			guard: false
		})
	},

	initializeAdjustTimesZones: function() {
		this.$('[name=timeZoneToggle]').prop('checked', !!this.options.field.timeZone)
		this.onChangeTimeZoneToggle()
	},

	renderFieldType: function() {
		this.fieldTypeDropdown = this.$('[name=fieldType]')
		const options = this.getFieldTypeDropdownOptions()
		this.renderFieldTypeDropdown(options)
	},

	renderFieldTypeDropdown: function(options) {
		this.fieldTypeDropdown?.empty()
		let fieldType = getFieldType({
			fieldType: this.field.fieldType,
			hasColumnOptions: this.hasColumnOptions()
		})
		dropdown.idLabels(this.fieldTypeDropdown, options, fieldType)
		this.initialFieldType = fieldType
	},

	hasColumnOptions: function() {
		return this.$('[name=column]').get(0).options.length > 0
	},

	getFieldTypeDropdownOptions: function() {
		const options = enums.toOptionData('datasetFieldType', [
			DATASET_FIELD_TYPE.CALCULATED, 'generic', DATASET_FIELD_TYPE.CLUSTER, 'generic'
		])
		// #46001 prevent adding a new basic field when there's a data source and no more columns
		const hasDataSourceWithNoMoreColumns = !this.model.hasNoDataSource() &&
			!this.hasColumnOptions()
		// #45813 if there's no data source, we use manual columns instead of basic ones
		if (hasDataSourceWithNoMoreColumns || this.model.hasNoDataSource()) {
			_.remove(options, option => option.id === 'basic')
		}
		let reorderedOptions
		if (hasDataSourceWithNoMoreColumns || this.model.hasNoDataSource()) {
			_.remove(options, option => option.id === 'basic')
			let reorderById = ['manual', 'calculated', CLUSTER]
			reorderedOptions = reorderById.map(item => options.find(option => option.id === item))
		} else {
			let reorderById = ['basic', 'manual', 'calculated', CLUSTER]
			reorderedOptions = reorderById.map(item => options.find(option => option.id === item))
		}
		return reorderedOptions
	},

	hasNoAutomaticFieldsOrDataSource: function() {
		const metadata = this.model.get('dataSourceMetadata')
		const automaticFields = _.get(metadata, 'fields') || []
		return _.isEmpty(automaticFields) ||
			this.model.hasNoDataSource()
	},

	renderColumnDropdown: function() {
		const metadata = this.model.get('dataSourceMetadata')

		const columnOptions = _(metadata.fields)
			// only include columns that don't already have fields
			.filter(automaticField => {
				// don't filter if we are editing a field and this one is it
				// or the field already exists on the dataset
				return this._fieldIsBeingEdited(automaticField) ||
					!this.model.getBasicFieldsByIntermediateTableColumn()[automaticField.intermediateColumnName]
			})
			.sortBy(automaticField => automaticField.label.toLowerCase())
			.map(automaticField => {
				return _.extend({}, automaticField, {
					id: automaticField.intermediateColumnName,
					label: automaticField.label,
					selected: !!this._fieldIsBeingEdited(automaticField)
				})
			}).value()

		// set the default value for the dropdown only when we are editing a field
		const defaultColumnOption =_.find(columnOptions,option =>
			this._fieldIsBeingEdited(option))
		const defaultValue = defaultColumnOption && defaultColumnOption.name

		const el = this.$('[name=column]')
		dropdown.idLabels(el, columnOptions, defaultValue, {
			// #62951 this is the "raw" column name, so it should be rendered
			// as-is, not localized
			localizeLabels: false
		})
		this.columnDropdown = new MarkedField({
			el,
			validator: columnOptions.length > 0 ? requiredValidator : false
		}).render()
		this.markedFields.push(this.columnDropdown)

		const defaultField =
			this.model.getBasicFieldsByIntermediateTableColumn()[defaultColumnOption?.intermediateColumnName]
		// show column or calculated based on the default value
		const fieldType = _.result(this.options.field, 'fieldType')
		const defaultFieldType = _.result(defaultField, 'fieldType')
		if (fieldType !== 'basic' || defaultFieldType !== 'basic' ||
			columnOptions.length === 0) {
			this.$('.column').addClass('is-not-displayed')
		} else {
			this.$('.equation').addClass('is-not-displayed')
		}

		if (columnOptions.length < 1) {
			el.addClass('disabled')
			this.$('.column').on('click', () => {
				if (!_.isNil(this.columnDropdownTooltip)) return

				this.columnDropdownTooltip = new Tooltip({
					attachTo: this.$('.type'),
					placement: 'below',
					arrow: 'center',
					content: i18n.get('datasets.field.fieldType.basic.noMoreToAdd.tooltip')
				}).render()
			})
		}

		this.initialColumnValue = el.val()
		this.columnOptions = columnOptions
	},

	/**
	 * if we passed in a field object, we're editing a field instead of adding a field
	 * if we found the same field in the dataset, it is being edited
	 * @param automaticField the field to determine is being edited
	 * @private
	 */
	_fieldIsBeingEdited: function(automaticField) {
		const matchedDatasetField =
			this.model.getBasicFieldsByIntermediateTableColumn()[automaticField.intermediateColumnName]
		return !_.isEmpty(this.options.field) &&
			(matchedDatasetField && this.options.field.id === matchedDatasetField.id)
	},

	/**
	 * Indicates whether a field is contained in another field's data cluster
	 * @param field the field that we are checking
	 */
	fieldContainedInCluster: function(field) {
		let clusteredDatasetFieldIds = _(this.model.get('fields'))
		  .filter(it => it.dataType === CLUSTER)
		  .map(it => it.clusteredFieldIds)
		  .flatten()
		  .value()
		return _.includes(clusteredDatasetFieldIds, field.id)
	},

	renderEquationButton: function() {
		this.setEquationButton = new EditableButton({
			el: this.$('.set-equation'),
			label: this.field.equation,
			extraClasses: 'set-equation',
			message: 'scorecards.node.metric.equation.set'
		}).render()
	},

	renderNameInput: function() {
		this.labelField = new MarkedField({
			el: this.$('[name=label]'),
			validators: [
				requiredValidator,
				fieldNameValidator
			],
		}).render()
		this.markedFields.push(this.labelField)

		// set the initial name
		let name = _.get(this.field, 'label')
		// #55521 if we're adding a new field we want to use the AutomaticField's label
		// not the column name
		if (_.isNil(name)) {
			const columnName = this.$('[name=column]').val()
			const metadata = this.model.get('dataSourceMetadata')
			const automaticField = metadata?.automaticFieldsByIntermediateColumn?.[columnName]
			name = automaticField?.label ?? columnName
		}
		this.$('[name=label]').val(name)
		// Store the initial value for when we change the field type
		this.initialNameValue = name
	},

	renderCalendarDropdown: function() {
		const el = this.$('[name=defaultCalendarId]')
		dropdown.idLabels(el,calendars.calendars,
			config.ytdCalendarId)
		this.defaultCalendarIdDropdown = new MarkedField({ el, }).render()
		this.markedFields.push(this.defaultCalendarIdDropdown)
	},

	renderRollupTreesDropdown: function() {
		var select = this.$('[name=treeId]')
		_.each(this.options.trees, function(tree) {
			select.append(new Option(tree.treeLabel, tree.id))
		})
	},

	renderCurrencyCodeDropdown: function() {
		if (_.size(currency.all()) > 1) {
			dropdown.currency(this.$('[name=currencyCode]'))
		} else {
			// or remove it if there's only one currency
			this.$('label.currency-code').remove()
		}
		dropdown.precision(this.$('[name=precision]'))
	},

	setClusteredFieldExcludedTypes: function(options) {
		if (!this.fieldContainedInCluster(this.field)) return

		// only allow clustered fields to be yes/no or numeric
		const superTypes = _.map(dataSuperTypes, type => type.id)
		options.excludedSuperTypes = _.concat(options.excludedSuperTypes,
			_.filter(superTypes, type =>
				type !== 'numeric' && type !== YES_NO))
	},

	setExcludedTypesForLockedDataType: function(options) {
		const existingDataType = this.originalField?.dataType
		if (!this.isLockedDataType()) return

		const superTypes = _.map(dataSuperTypes, type => type.id)
		const subTypes = _.map(dataTypes, type => type.id)

		options.excludedSuperTypes = superTypes
		options.excludedDataTypes = subTypes
		if (this.canChangeDataTypesWithLockedDataType()) {
			const validDataTypesToChangeTo = getValidDataTypesToChangeTo(existingDataType)
			const validSupertypesToChangeTo = new Set(_.map(validDataTypesToChangeTo, dataType =>
				getDataSuperTypeForDataType(dataType)))

			options.excludedSuperTypes = _.filter(superTypes, superType =>
				!validSupertypesToChangeTo.has(superType))
			options.excludedDataTypes = _.filter(subTypes, type =>
				!validDataTypesToChangeTo.includes(type))
		}
	},

	/**
	 * #54021 if the user changed a data type for a locked field (e.g. number to postal code)
	 * when the field form was opened previously, but then they closed the field form and re-opened
	 * it without saving the dataset, we need to allow the user to change the data type back to the original one
	 */
	setExcludedTypesForLockedDataTypeWithUnsavedChanges: function(options) {
		const existingField = this.options?.field
		const hasUnsavedLockedDataTypeChange = !!existingField?.hasUnsavedLockedDataTypeChange
		if (!hasUnsavedLockedDataTypeChange) return

		const validDataTypesToChangeFrom = getValidDataTypesToChangeFrom(existingField.dataType)
		// the data type the user changed to is a valid data type to change when the field is locked
		validDataTypesToChangeFrom.forEach(fromDataType => {
			// allow the super type and data type to be selected again
			const superTypeForFromType = dataSuperTypesByDataType[fromDataType]
			options.excludedSuperTypes = _.filter(options.excludedSuperTypes,
				superTypeId => superTypeId !== superTypeForFromType.id)
			options.excludedDataTypes = _.filter(options.excludedDataTypes,
				dataType => fromDataType !== dataType)
		})
	},

	setInitialValueForDataTypeSelector: function(options) {
		const existingDataType = this.options?.field?.dataType
		// don't exclude the currently selected super type or data type
		options.excludedSuperTypes = _.filter(options.excludedSuperTypes, superType =>
			dataSuperTypesByDataType[existingDataType]?.id !== superType)
		options.excludedDataTypes = _.filter(options.excludedDataTypes,type =>
			type !== existingDataType)
		if (!_.isNil(options.initialValue)) return

		const column = this.columnDropdown.val()
		const dataSourceMetadata = this.model.get('dataSourceMetadata')
		const automaticField = _.get(
			dataSourceMetadata?.automaticFieldsByIntermediateColumn,
			column, null)
		if (_.isNil(automaticField)) return

		options.initialValue = automaticField && automaticField.dataType
		if (!_.isNil(options.initialValue)) return

		const validDataTypes = _.filter(dataTypes, dataType =>
			!_.includes(options.excludedDataTypes, dataType.id))
		options.initialValue = validDataTypes[0]?.id
	},

	getDataTypeSelectorOptions: function() {
		const existingDataType = this.options?.field?.dataType
		const currentDataType = this.field?.dataType
		const options = {
			excludedSuperTypes: [],
			excludedDataTypes: [],
			initialValue: existingDataType || currentDataType
		}

		this.setClusteredFieldExcludedTypes(options)
		this.setExcludedTypesForLockedDataType(options)
		this.setExcludedTypesForLockedDataTypeWithUnsavedChanges(options)
		this.setInitialValueForDataTypeSelector(options)

		return options
	},

	renderDataTypeSelect: function() {
		_.invokeMap([this.dataTypeSelector, this.dataSuperTypeDropdown,
			this.dataTypeDropdown], 'remove')
		_.remove(this.markedFields, markedField =>
			markedField === this.dataSuperTypeDropdown ||
			markedField === this.dataTypeDropdown)

		const options = this.getDataTypeSelectorOptions()
		_.extend(options, {
			dataTypeOptionsGenerator: this.dataTypeOptionsGenerator.bind(this),
		})
		this.dataTypeSelector = new DataTypeSelector(options)
		this.dataTypeSelector.on('change:dataType', this.changeDataType.bind(this))

		this.$('.data-type-selector').append(this.dataTypeSelector.el)
		this.dataTypeSelector.render()

		this.dataSuperTypeDropdown = new MarkedField({
			el: this.$('[name=dataSuperType]'),
			validator: val => {
				const isNewField = !_.get(this.options, 'field.id')
				const isCalculated = this.fieldTypeDropdown.val() === 'calculated'
				if (val === DATASET_LINK && isNewField && isCalculated) {
					return i18n.get('datasets.linked.calculated.finishBuilding.new')
				}
			}
		}).render()
		this.markedFields.push(this.dataSuperTypeDropdown)

		// filter out the clusteredFieldIds selector since it does not need
		// constant updating with every data type change
		const el = this.$('input, select').filter(':not(.clustered-field-ids)')
		formHelper.writeToDom(this.field, el)

		// #49745 wrap data type select in a MarkedField to have the same disabled
		// appearance/behavior as the dataSuperTypeDropdown
		this.dataTypeDropdown = new MarkedField({
			el: this.$('[name=dataType]')
		}).render()
		this.markedFields.push(this.dataTypeDropdown)

		// after formHelper.writeToDom
		selects(this._unconfiguredSelects())
		// after the call to selects
		this.renderFieldGroupDropdown()
		this.renderLinkedDatasetSelector()

		this.changeDataType()
		return this
	},

	renderMissingValueTreatment: function() {
		const select = this.$('[name=missingValueTreatment]')
 		dropdown.mvt(select, this.field.missingValueTreatment, { includeNa: false })
	},

	toggleMissingValueTreatment: function() {
		let fieldType = this.$('[name=fieldType]').val()
		let showMvt = fieldType === 'calculated'
		this.$('.missing-value-treatment')
			.toggleClass('is-not-displayed', !showMvt)
	},

	isLockedDataType: function() {
		return this.options.field && this.options.field.lockedDataType
	},

	canChangeDataTypesWithLockedDataType: function() {
		const existingDataType = this.options?.field?.dataType
		const allowSwitchingBackToPreviousDataType = !!this.options?.field?.hasUnsavedLockedDataTypeChange &&
			hasValidTypeToChangeFrom(existingDataType)

		// #54021 we allow some data types to be changed one way (or both if there's 2 opposite toAndFrom entries)
		// for example, we allow number fields to be changed to postal codes but not the other way around
		return hasValidTypeToChangeTo(existingDataType) ||
			// #54021 if the user changed the data type on a locked field, closed, and re-opened the
			// field form without saving the dataset, give them a way to undo that change
			allowSwitchingBackToPreviousDataType ||
			// we allow changing types for lockedDataType fields between numeric types
			isNumericDataType(existingDataType)
	},

	_maybeApplyLockedDataTypeState: function() {
		const existingDataType = this.options?.field?.dataType

		if (this.isLockedDataType()) {
			// note can't call this._disableControl because we need to call
			// disabled on the marked field, not on the DOM element
			if (!this.canChangeDataTypesWithLockedDataType()) {
				this._disableMarkedField(this.dataTypeDropdown, () => {
					this.dataTypeTooltip && this.dataTypeTooltip.remove()
					this.dataTypeTooltip = this._displayLockedFieldPropertyTooltip(
						'datasets.field.lockedDataType', this.$('.select.data-type'))
				})
				this._disableMarkedField(this.dataSuperTypeDropdown, () => {
					this.dataTypeTooltip && this.dataTypeTooltip.remove()
					this.dataTypeTooltip = this._displayLockedFieldPropertyTooltip(
						'datasets.field.lockedDataType', this.$('.select.data-super-type'))
				})
			}

			// #46150 prevent changing the calendar if this is a calendar period field with
			// a locked data type
			if (existingDataType === CALENDAR_PERIOD) {
				this._disableMarkedField(this.defaultCalendarIdDropdown, () => {
					this.defaultCalendarIdTooltip && this.defaultCalendarIdTooltip.remove()
					this.defaultCalendarIdTooltip = this._displayLockedFieldPropertyTooltip(
						'datasets.field.lockedCalendarId', this.defaultCalendarIdDropdown.$el)
				})

			}

			// #49855 if the data type is locked, don't allow changing the field's equation either
			this._disableMarkedField(this.columnDropdown, () => {
				this.columnDropdownTooltip && this.columnDropdownTooltip.remove()
				this.columnDropdownTooltip = this._displayLockedFieldPropertyTooltip(
					'datasets.field.lockedColumn', this.columnDropdown.$el)
			})
			this._disableControl(this.setEquationButton.$el, () => {
				this.equationButtonTooltip && this.equationButtonTooltip.remove()
				this.equationButtonTooltip = this._displayLockedFieldPropertyTooltip(
					'datasets.field.lockedEquation', this.setEquationButton.$el)
			})

			// #54498 don't allow changing to a calculated or a cluster field type
			// if the data type is locked
			let options = this.getFieldTypeDropdownOptions()
			const fieldType = this.fieldTypeDropdown.val()
			const isCalculated = fieldType === DATASET_FIELD_TYPE.CALCULATED
			const isCluster = fieldType === DATASET_FIELD_TYPE.CLUSTER
			if (isCalculated || isCluster) {
				options = _.filter(options, option => option.id === fieldType)
			} else {
				options = _.filter(options, option =>
					option.id !== DATASET_FIELD_TYPE.CALCULATED &&
					option.id !== DATASET_FIELD_TYPE.CLUSTER)
			}
			this.renderFieldTypeDropdown(options)
		}
	},

	_disableMarkedField(markedField, handler) {
		markedField.disable()
		this._disableControl(markedField.$el, handler)
	},

	_disableControl: function(control$, handler) {
		disableControls(control$)
		control$.on('click', handler)
	},

	_displayLockedFieldPropertyTooltip: function(msgCode, attachTo) {
		const isPk = this.options.field && this.options.field.primaryKey
		_.result(this.dataTypeTooltip, 'remove')
		const content = isPk ? i18n.get('datasets.primaryKey.notEditable.supplemental') : i18n.get(msgCode)
		return new Tooltip({
			attachTo,
			content,
			placement: 'below',
			arrow: 'center'
		}).render()
	},

	dataTypeOptionsGenerator: function() {
		const dataTypeSelectorOptions = this.getDataTypeSelectorOptions()
		const validDataTypes = _.filter(dataTypes, dataType =>
			!_.includes(dataTypeSelectorOptions.excludedDataTypes, dataType.id))
		const options = _.map(validDataTypes, dataType =>
			_.extend({}, dataType, {
				label: i18n.get(dataType.label),
				group: i18n.get(dataType.group),
				selected: dataType.id === dataType,
				dataset: {icon: dataType.icon}
			}))
		return options
	},

	renderFieldGroupDropdown: function() {
		let options = _.map(this.model.getFieldGroups(), group => {
			return {
				label: group,
				value: group,
				selected: this.field.group === group
			}
		})

		selects.filtering(this.$('[name=group]'), {
			options,
			render: {
				create: function (d) {
					// The create new option at the bottom
					return i18n.get('common.add2', d.label)
				}
			},
			placeholder: i18n.get('datasets.field.group.chooseOrCreate'),
			filterSuggestion: i18n.get('datasets.field.group.chooseOrCreate')
		})
	},

	renderLinkedDatasetSelector: function() {
		_.invoke(this.linkedDatasetSelector, 'remove')

		const linkedDatasetId = _.get(this.options, 'field.linkedDatasetId')
		const linkedDataset = this.datasetsById[linkedDatasetId]
		if (!_.isNil(linkedDataset)) {
			this.linkedDataset = new Dataset(linkedDataset)
		}

		let selectorOptions = _.extend(
			datasetSelectorOptions.forButton({ dataset: this.linkedDataset }),
			{ message: i18n.get('report.designer.chooseDataset')})

		selectorOptions.testSelectedObject = (selectedDataset) => {
			const selectedDatasetModel = new Dataset(selectedDataset)
			let result = datasetSelectorOptions.testRealDataset(selectedDataset)
			if (result) {
				return result
			}

			let datasetId = _.get(selectedDataset, 'id')
			if (datasetId) {
				if (datasetId == this.model.id) {
					return i18n.get('datasets.linked.differentDataset')
				}

				if (!selectedDatasetModel.hasPrimaryKeyField()) {
					return i18n.get('datasets.linked.primaryKey.required')
				}
			} else {
				return i18n.get('datasets.linked.linkedDataset.required')
			}
		}

		this.linkedDatasetSelector = new ObjectSelectorButton(selectorOptions)

		this.linkedDatasetSelector.on('change', selected => {
			this.updateKeySet()
			this.formView.change()
			// Call validate so that "Linked Dataset is required" error is cleared
			// if it was there before selecting a dataset
			this.formView.validate()
		})

		this.$('.linked-dataset').append(this.linkedDatasetSelector.el)
		this.linkedDatasetSelector.render()
	},

	getLinkedDatasetId: function () {
		if (!this.linkedDatasetSelector) return undefined
		return _.get(this.linkedDatasetSelector.val(), 'id')
	},

	renderTimeZoneSelect: function() {
		renderTimeZoneSelect(
			this.$('[name=timeZone]'),
			'datasets.fields.dateTime.zone',
			this.field.timeZone || momentHelper.defaultTimeZone()
		)
	},

	renderTimeDisplayTypeSelect: function() {
		renderTimeDisplayTypeSelect(
			this.$('[name=timeDisplayType]'),
			this.field.timeDisplayType,
			[{
				value: 'default',
				label: i18n.get('admin.appConfiguration.generalSettings.timeFormat.default'),
				selected: this.field.timeDisplayType === undefined
			}]
		)
	},

	renderClusteredFieldsSelect: function() {
		const fields = unwrapModel(this.model).fields
		const clusteredFieldIds = (this.options.field.clusteredFieldIds || [])
			.map(it => parseInt(it))

		const selectedFieldIds = new Set(clusteredFieldIds)
		const clusterableDataTypes = [NUMBER, YES_NO, CURRENCY, PERCENT]
		const options = fields
			.filter(({dataType}) => _.includes(clusterableDataTypes, dataType))
			.map(({id, label}) => ({
				value: id,
				label,
				selected: selectedFieldIds.has(id)
			}))
		const el = this.$('[name=clusteredFieldIds]')
		selects.filtering(el, {
			multiple: true,
			options
		})
		this.clusterFieldsMarkedField = new MarkedField({
			el,
			validator: requiredValidator
		}).render()
		this.markedFields.push(this.clusterFieldsMarkedField)
		el.on('change', e => {
			this.toggleClusterAnalyzeButton(!$(e.currentTarget).val().length)
		})
		this.toggleClusterAnalyzeButton(!el.val().length)
	},

	toggleClusterAnalyzeButton: function(isDisabled) {
		this.$('.cluster-analyze').toggleClass('is-disabled', isDisabled)
	},

	renderNumClustersSelect: function() {
		const clusterFields = this.$('[name=numClusters]')
		clusterFields.append(new Option(i18n.get('scorecards.field.numClusters.auto'), ''))
		_.range(2, maxClusterCount).forEach(count => {
			clusterFields.append(new Option(count, count))
		})
		const numClusters = this.field.numClusters
		const autoCluster = this.field.autoCluster
		// #39965 If we're auto clustering, we ignore any numClusters and display
		// the auto option - which is also the default option.
		const value = !autoCluster && numClusters ? `${numClusters}` : ''
		clusterFields.val(value)
	},

	renderEquationForm: function() {
		this.saveState()
		this.equationForm = new FieldFormEquationForm({
			model: this.model,
			startingDatasetId: this.model.id,
			field: this.field,
			individualRecords: true,
			datasets: this.options.datasets,
			treeItemsById: this.treeItemsById
		}, this.context())
		this.equationForm.on('done', data =>  {
			this.formView.clearErrors()
			this.formView.change()
			_.extend(this.field, data)
			if (_.isEmpty(this.field.equation)) {
				delete this.field.datasetAggregations
			} else {
				this.field.aggregations = data.datasetAggregations
				this.field.datasetAggregations = data.datasetAggregations
			}
			this.setEquationButton.renderLabel(this.field.equation)
		})
		appDisplay.dialog(this.equationForm)
	},

	setFieldType: function(event) {
		// If the field type is basic, we want to store its current label
		// so that we can use it when switching back to a basic value type
		if (this.field?.fieldType === 'basic') {
			this.lastBasicLabelValue = this.$('[name=label]').val()
		}

		const fieldType = this.$('[name=fieldType]').val()
		// #49712 #49781 update this.field with the selected fieldType,  so it
		// won't be overwritten when this.renderDataTypeSelect() calls formHelper.writeToDom
		this.field.fieldType = fieldType

		const shouldShowColumnDropdown = fieldType === 'basic' &&
			!this.hasNoAutomaticFieldsOrDataSource()
		this.$('.column').toggleClass('is-not-displayed', !shouldShowColumnDropdown)

		this.$('.equation')
			.toggleClass('is-not-displayed', fieldType !== 'calculated')

		this.$('.cluster-fields, .cluster-count, .cluster-analyze')
			.toggleClass('is-not-displayed', fieldType !== CLUSTER)
		this.$('.data-super-type-container, .data-type-specific-fields')
			.toggleClass('is-not-displayed', fieldType === CLUSTER)
		this.toggleAggregationType()

		// default editable back to what it initially was when the form was first rendered
		// any time the field type is changed, because they could be changing from manual
		// to something else, and it would be jarring to have the editable state from manual stay checked
		let { editable } = this.editableFieldChecker.getDefaultEditableAndEditableDisabled(this.field)
		if (this.isEditingField()) {
			editable = _.get(this.options.field, 'editable', false)
		}
		this.field.editable = editable
		this.renderEditable()

		if (this.options.wizard) {
			const steps = fieldType === CLUSTER ? 2 : 1
			if ((event && event.type == 'change') || this.options.wizard.steps != steps) {
				this._renderWizard({
					el: this.el,
					steps: steps
				})
			}
		}

		// #49491 re-render the data type select, so if we're changing to a calculated
		// from a basic field, all the data types show instead of just the valid ones for a basic field
		// #50782 don't re-render the data type select when changing to
		// "Data Clusters" (it has already been hidden, so don't unhide it)
		if (fieldType !== CLUSTER) {
			this.renderDataTypeSelect()
		}
		this.maybeDisplayManualFieldErrorBanner()
	},

	changeFieldType: function(e) {
		this.setFieldType(e)
		this.setFieldTypeLabel()
		this.renderEditable()
		this._maybeApplyLockedDataTypeState()
		this.toggleMissingValueTreatment()
	},

	/**
	 * Set the label field of the field type dropdown to the appropriate value
	 * based on the type chosen. If the field type is NOT 'basic', equivalent to
	 * the 'Column from Source Data' option, and the label text matches a
	 * column option, we want to clear the label field by default.
	 */
	setFieldTypeLabel: function() {
		const currentLabelValue = this.$('[name=label]').val()
		const fieldType = this.$('[name=fieldType]').val()
		const isInitialLabelValue =
			this.initialNameValue === currentLabelValue
			|| _.isEmpty(currentLabelValue)

		if (fieldType === 'basic') {
			// Prioritize using the currenLabelValue if it is not blank followed
			// by lastBasicLabelValue and finally the initialNameValue
			this.$('[name=label]').val(currentLabelValue ? currentLabelValue :
				this.lastBasicLabelValue ?? this.initialNameValue)
		} else if (fieldType === this.initialFieldType && isInitialLabelValue) {
			// #60088
			// If the initial field type is not basic and matches a column
			// option, we want to ensure the label field is preserved so long
			// as it matches that initial value
			this.$('[name=label]').val(this.initialNameValue)
		} else {
			// Default a non-basic value to blank if the name matches an
			// existing column option
			if (this.isMatchingColumnName(currentLabelValue)) {
				this.$('[name=label]').val('')
			}
		}
	},

	isEditingField: function() {
		return !_.isNil(this.options.field)
	},

	renderEditable: function() {
		const editableContainer = this.$('.editable-label')

		const hideEditableCheckbox = this.editableFieldChecker.isNotEditableField(this.field)
		editableContainer.toggleClass('is-not-displayed', hideEditableCheckbox)
		if (hideEditableCheckbox) return

		let { editable, disabled } = this.editableFieldChecker.getDefaultEditableAndEditableDisabled(this.field)
		this.fieldModel.set('editable', editable)
		editableContainer.find('input').prop('checked', editable)
		// we're showing the checkbox, set it's disabled state
		editableContainer.prop('disabled', disabled)
		if (!disabled) {
			unDisableCheckbox(editableContainer)
			return
		}

		// disable the checkbox
		let msgCode = this.editableFieldChecker.getMessageCodeForDisabledEditable(this.field)
		// if they're trying to add a manual field to a dataset without a PK and the
		// dataset has a data source, then we need to show an error banner
		if (this.shouldDisplayManualFieldErrorBanner()) {
			msgCode = pkRequiredForManualFieldErrorCode
		}
		disableCheckbox(editableContainer, i18n.get(msgCode))
	},

	maybeDisplayManualFieldErrorBanner: function() {
		if (!this.shouldDisplayManualFieldErrorBanner()) return

		const message = i18n.get(pkRequiredForManualFieldErrorCode)
		this.formView.errorMessage(message)
	},

	shouldDisplayManualFieldErrorBanner: function() {
		const needsPk = !this.editableFieldChecker.hasPrimaryKey() && !this.model.hasNoDataSource()
		return this.editableFieldChecker.isManualField(this.field) && needsPk
	},

	/**
	 * Determines if field name was initially set to match its column name and
	 * whether it matches an existing column name option.
	 * @param name - the field name to check
	 * @returns {boolean} - whether the field nam was initially set to match its
	 * column name and matches a column name option
	 */
	isMatchingColumnName(name) {
		const isInitialColumnName = this?.initialNameValue?.toLowerCase()
			=== this?.initialColumnValue?.toLowerCase()
		const matchesSomeColumnOption =
			this.columnOptions?.some(item =>
				item?.intermediateColumnName?.toLowerCase() ===
				name?.toLowerCase())
		return isInitialColumnName && matchesSomeColumnOption
	},

	changeColumn: function() {
		// default the name to the column's name
		const columnName = this.$('[name=column]').val()
		const name = this.$('[name=label]').val()

		// #55631
		// If the field name was initially set to match the column name and the
		// current name matches a column option, update the name to match the
		// newly selected column option.
		if (this.isMatchingColumnName(name)) {
			this.$('[name=label]').val(columnName)

			// If we change to a non-basic value, we want to make sure re-selecting
			// the basic value type will default to the newly set column name
			this.lastBasicLabelValue = columnName
		}

		// default the dataType to the field's dataType
		let field = this.model.getBasicFieldsByIntermediateTableColumn()[columnName]
		// fields that are already on the dataset get precedence, since
		// dataTypes from dataSourceMetadata have the ORIGINAl dataType
		if (!field) {
			field = this.metadataFieldsByName[columnName]
		}
		if (field && field.dataType) {
			this.dataTypeSelector.setDataType(field.dataType)
		}

		this.renderDataTypeSelect()
	},


	onChangeTimeZoneToggle: function() {
		const timeZoneToggle = this.$('[name=timeZoneToggle]').prop('checked')
		this.$('label.time-zone').toggleClass('is-not-displayed', !timeZoneToggle)
	},

	onChangeNumClusters: function() {
		this._renderWizard({ el: this.el })
	},

	onChangeClusteredFieldIds: function() {
		this._renderWizard({ el: this.el })
	},

	onClickAnalyzeClusters: function(e) {
		// If no fields are selected, display a tooltip stating that
		// clustering fields are required.
		let currentTarget = $(e.currentTarget)
		if (currentTarget.hasClass('is-disabled')) {
			this.disabledButtonTooltip = new Tooltip({
				closeOnChangedFocus: true,
				attachTo: currentTarget,
				placement: 'above',
				arrow: 'center',
				content: i18n.get('datasets.field.fieldType.cluster.disabled.tooltip')
			}).render()
			return
		}

		let request = {
			fieldIds: this.$('[name=clusteredFieldIds]').val()
		}
		let xhr = $.post({
			url: `api/datasets/${this.model.id}/clusters`,
			data: JSON.stringify(request),
			error: this.formView.error.bind(this.formView)
		})
		appDisplay.dialog(xhr, response =>
			new AnalyzeClusters(response))
	},

	toggleAggregationType: function(dataType) {
		// aggregation type is only for date, time, datetime and numeric fields
		// #45832 except for "calendar period" data type
		let showAggregationType = isDateTimeDataType(dataType) || isNumericDataType(dataType)
		this.$('label.aggregation-type').toggleClass('is-not-displayed',
			!showAggregationType)
		if (showAggregationType) {
			dropdown.idLabels(this.$('[name=aggregationType]').empty(),
				this.getAggregationTypes(dataType), this.field.aggregationType)
		}
	},

	/**
	 * update what's shown on the form based on what's chosen in the data type
	 * dropdown
	 */
	changeDataType: function() {
		_.result(this.labelField, 'repositionTooltip')
		this.dataSuperTypeDropdown.validate()

		var dataType = this.dataTypeSelector.getDataType()
		this.fieldModel.set('dataType', dataType)
		this.dataType = dataType
		this.field.dataType = dataType

		if (dataType === ROLLUP) {
			if (_.isEmpty(this.options.trees)) {
				this.$('label.tree').addClass('is-not-displayed')
				var html = i18n.get('datasets.trees.required', adminTreesLink())
				this.$('.banner').html(html).removeClass('is-not-displayed')
				this.$el.addClass('has-error')
			}
		} else {
			this.$('.banner').addClass('is-not-displayed')
			this.$el.removeClass('has-error')
		}
		this.toggleAggregationType(dataType)
		// if this is a new field, default percent fields to average aggregation type
		if (!this.options.field.id && dataType === 'percent') {
			this.$('[name=aggregationType]').val('average')
			selects.update(this.$('[name=aggregationType]'))
		}
		this.changeAggregationType()

		// currency code is only for currency fields
		this.$('label.currency-code').toggleClass('is-not-displayed', dataType !== CURRENCY)

		// default range calendar is only for date fields
		const isCalendarPeriod = dataType === CALENDAR_PERIOD
		const isDateOrCalendarPeriod = dataType === DATE || isCalendarPeriod
		const calendarLabel$ = this.$('label.calendar')
		if (isDateOrCalendarPeriod) {
			const labelMsgKey = isCalendarPeriod ? 'common.calendar.calendar' :
				'datasets.field.defaultRangeCalendar'
			calendarLabel$.find('.label-text').text(i18n.get(labelMsgKey))
		}
		calendarLabel$.toggleClass('is-not-displayed', !isDateOrCalendarPeriod)


		// keys are only for rollup trees
		this.$('label.show-keys').toggleClass('is-not-displayed', dataType !== ROLLUP)
		// tree is only for rollup trees
		this.$('label.tree').toggleClass('is-not-displayed', dataType !== ROLLUP)
		this.updateKeySet()

		// show datasets dropdown if appropriate
		this.$('.linked-dataset').toggleClass('is-not-displayed', dataType !== DATASET_LINK)

		// Only show precision dropdown for numeric data types
		this.$('.precision').toggleClass('is-not-displayed', !isNumericDataType(dataType))

		// time zone adjustment is only for date-time fields
		this.$('label.adjust-times')
			.toggleClass('is-not-displayed', dataType !== DATE_TIME)

		// time format is only for time or date-time fields
		this.$('label.time-display-type').toggleClass('is-not-displayed',
				!_.includes([DATE_TIME, TIME], dataType))

		this.$('.geographic-ancestor-selector-container')
			.toggleClass('is-not-displayed', !this.requiresLocationTreeNodeId())
	},

	requiresLocationTreeNodeId: function() {
		return getRequiredGeographicAncestorTypes(
			this.dataTypeSelector.getDataType()).length > 0
	},

	getAggregationTypes: function(dataType) {
		const isDateTimeOrCalendarPeriod = isDateTimeDataType(dataType) ||
			dataType === CALENDAR_PERIOD
		let aggregationTypes = isDateTimeOrCalendarPeriod ?
			aggregationTypeOptions.nonTotalColumn(dataType) :
			aggregationTypeOptions.forFieldEdit()
		let pkFieldId = this.model.get('primaryKeyFieldId')
		let rollupPk = pkFieldId &&
			isGeographicOrRollup(this.model.combinedFieldsById[pkFieldId].dataType)
		if (!rollupPk) {
			// #43313 None aggregation type only makes sense when the primary
			// key is a rollup field
			_.remove(aggregationTypes, aggregationType =>
				aggregationType.id === 'none')
		}
		return aggregationTypes
	},

	/**
	 * When the aggregation type changes, changes the label for the field.  If
	 * the aggregation type is none, the label is "Aggregation Type".
	 * Otherwise, it's "Default Aggregation Type"
	 */
	changeAggregationType: function() {
		let aggregationType = this.$('[name=aggregationType]').val()
		let label = aggregationType === 'none' ?
			i18n.get('scorecards.node.metric.aggregationType.aggregationType') :
			i18n.get('scorecards.node.metric.threshold.default.withArg', 'report.customReport.aggregationType')
		this.$('label.aggregation-type .label-text').text(label)
	},

	// update the form based on what's chosen in the tree dropdown
	updateKeySet: function() {
		let dataType = this.dataTypeSelector.getDataType()
		let keySetId = _.get(this.options, 'field.keySetId')
		if (dataType === DATASET_LINK) {
			let linkedDatasetId = this.getLinkedDatasetId()
			// #30884 use strict equals for Safari
			if (linkedDatasetId && _.get(this.linkedDataset, 'id') === linkedDatasetId) {
				let pkFieldId = this.linkedDataset.get('primaryKeyFieldId')
				let pkField = this.linkedDataset.combinedFieldsById[pkFieldId]
				if (pkField) {
					keySetId = keySetId || pkField.keySetId
					this.toggleKeySet(pkField.dataType, pkField.treeId, keySetId)
				} else {
					// #53921 When Dataset Link is the saved data type, upon switching
					// data types back and forth, pkField can return null.
					// As a result, we need to toggle the key set regardless.
					this.toggleKeySet(dataType, null, keySetId)
				}
			} else if (linkedDatasetId) {
				this.formView.disableCancel = true // this prevents the primary button for re-enabling in formView._updateButtons
				this.formView.getPrimaryButton().toggle(false)

				let url = `api/datasets/${linkedDatasetId}?fields=true`
				this.linkedDatasetXhr = $.get(url, linkedDataset => {
					this.formView.getPrimaryButton().toggle(true)

					this.linkedDataset = new Dataset(linkedDataset)
					this.updateKeySet()
				})
			} else {
				// #53921 When the data type is changed to Dataset Link and
				// no linked dataset has been chosen prior, we still need to
				// toggle visibility
				this.toggleKeySet(dataType, null, keySetId)
			}
		} else {
			const treeDropdown = this.$('[name=treeId]')
			// calling .toString() to avoid type warnings
			const selectedTreeId = treeDropdown && treeDropdown.val() &&
				parseInt(treeDropdown.val().toString())
			let selectedKeySets = this.treesById[selectedTreeId] && this.treesById[selectedTreeId].keySets
			selectedKeySets = _.sortBy(selectedKeySets, 'id')
			if (selectedKeySets) {
				const keySetIdPresent = _.find(selectedKeySets, set =>
					set.id === keySetId) != null
				// if this is a different tree id,  or if there is only one key set, set that one
				if (!keySetIdPresent || selectedKeySets.length === 1) {
					keySetId = selectedKeySets[0] && selectedKeySets[0].id
				}
			}
			this.toggleKeySet(dataType, selectedTreeId, keySetId)
		}
	},

	toggleKeySet: function(dataType, treeId, keySetId) {
		var tree = this.treesById[treeId]
		const keySetDropdown = this.$('[name=keySetId]')
		keySetDropdown.empty()
		// key set is only for rollup trees that have more than one key set
		var showKeySet = dataType === ROLLUP && tree != null && _.size(tree.keySets) > 1
		this.$('label.key-set').toggleClass('is-not-displayed', !showKeySet)
		if (tree) {
			dropdown.idLabels(keySetDropdown, _.sortBy(tree.keySets, 'id'), keySetId)
		}
	},

	context: function() {
		const titleKey = this.isEditingField() ? 'common.edit2' : 'common.add2'
		const title = i18n.get(titleKey, 'datasets.field')

		return {
			title
		}
	},

	saveState: function() {
		const formData = formHelper.readFromDom(this.el)

		formData.dataType = formData.dataType || this.dataTypeSelector.getDataType()
		// #37796 Not strictly necessary, but avoids confusion as this field is
		// for front-end use only, and is abstracted from the backend.
		delete formData.dataSuperType

		// we have a basic field, change the equation to a basic one
		if (formData.fieldType === 'basic') {
			const column = !_.isNil(formData.column) ? formData.column : ''
			formData.equation = `[${column}]`
		}
		if (formData.fieldType === CLUSTER) {
			formData.dataType = CLUSTER
			if (formData.numClusters === '') {
				formData.autoCluster = true
			}
		}
		// if field.group is an empty string, the table breaks in datasets-edit.js
		if (!formData.group) {
			delete formData.group
			delete this.field.group
		}
		// the key set dropdown was hidden because there was only one key set,
		// but we still need to save it
		if (!formData.keySetId) {
			formData.keySetId = this.$('[name=keySetId]').val()
		}
		if (!formData.timeZoneToggle) {
			delete formData.timeZone
			delete this.field.timeZone
		}
		delete formData.timeZoneToggle

		formData.linkedDatasetId = this.getLinkedDatasetId()

		_.extend(this.field, formData)

		if (formData.timeDisplayType === 'default') {
			delete this.field.timeDisplayType
		}

		this._saveGeographicTypeState(formData)

		if (this.isChangingToValidDataTypeForLockedField()) {
			// #54021 we support changing from number to postal code, even if the data type is locked
			// and we need a way to let the user reverse this change if they haven't saved the dataset yet
			this.field.hasUnsavedLockedDataTypeChange = true
		}

		return this.field
	},

	isChangingToValidDataTypeForLockedField: function() {
		if (this.field?.fieldType !== DATASET_FIELD_TYPE.BASIC) return
		if (!this.isLockedDataType()) return

		const oldDataType = this.options?.field?.dataType
		const newDataType = this.field?.dataType
		return isCompatibleDataType(oldDataType, newDataType)
	},

	_saveGeographicTypeState: function(formData) {
		const dataSuperType = this.dataTypeSelector.getDataSuperType()
		const isGeographicDataType = dataSuperType &&
			dataSuperType.id === 'geographic'
		this.field.locationTreeNodeId = this.requiresLocationTreeNodeId() ?
			this.selectGeographicAncestor.getLocationTreeNodeId() : null
		if (isGeographicDataType) {
			if (this.field.geographicAncestorType) {
				if (this.field.geographicAncestorType === 'dataset-field') {
					delete this.field.locationTreeNodeId
				} else if (this.field.geographicAncestorType === 'specific-value') {
					delete this.field.countryFieldId
					delete this.field.stateProvinceFieldId
					delete this.field.cityFieldId
				}
				delete this.field.geographicAncestorType
			}
		} else {
			delete this.field.locationTreeNodeId
			delete this.field.countryFieldId
			delete this.field.stateProvinceFieldId
			delete this.field.cityFieldId
		}
	},

	renderLinkedDatasetConfirmPrompt: function(validationException, e) {
		if (!validationException) {
			return
		}

		const linkedDatasetConfirmPrompt = new LinkDatasetsConfirmPrompt({
			confirmLabel: 'datasets.link.create',
			cancelLabel: 'common.cancel',
			titleKey: 'datasets.link.plural',
			validationException,
			onConfirm: () => this._submitSuccess(e)
		})
		linkedDatasetConfirmPrompt.on('cancel', () => {
			this.formView.start()
		})
		appDisplay.dialog(linkedDatasetConfirmPrompt)
	},

	// this is only split out from renderSelectGeographicAncestor to make mocking in tests easier
	_createSelectGeographicAncestor: function() {
		this.selectGeographicAncestor = new SelectGeographicAncestor({
			model: this.model,
			field: this.fieldModel,
			countries: this.options.countries,
			stateProvinces: this.options.stateProvinces,
			cities: this.options.cities,
			geoSelections: this.options.geoSelections
		})
		this.$('.geographic-ancestor-selector-container').append(this.selectGeographicAncestor.el)
	},

	renderSelectGeographicAncestor: function() {
		this._createSelectGeographicAncestor()
		this.selectGeographicAncestor.render()
	},

	validateField: function(e) {
		return $.post({
			url: this.model.getValidateFieldUrl(),
			data: JSON.stringify({
				field: this.field,
				dataset: this.model
			}),
			error: (xhr) => {
				// #55752
				// hacky approach to keep validation of calculated fields within
				// FieldEquationValidator.java without having to bring that
				// validation logic into this file
				if (this.field.fieldType != 'calculated' &&
					xhr.responseJSON.messageCode == 'datasets.field.equation.invalidResultDataType') {
					xhr.responseJSON.messageCode  = 'datasets.field.equation.invalidResultDataType.column'
				}
				errorHandler(xhr, {
					formView: this.formView,
					handler: () => {
						const err = _.get(xhr, 'responseJSON')
						if (_.get(err, 'preludeCode') === 'datasets.linked.someFieldsMatch.prelude') {
							this.renderLinkedDatasetConfirmPrompt(err, e)
							return true
						}
					}
				})
			}
		})
	},

	test: function () {
		let dataType = this.dataTypeSelector.getDataType()
		if (dataType === DATASET_LINK && !this.linkedDatasetSelector.val()) {
			return i18n.get('datasets.linked.linkedDataset.required')
		}
		if (this.shouldDisplayManualFieldErrorBanner()) {
			return i18n.get(pkRequiredForManualFieldErrorCode)
		}
	},

	onClickSaveWithoutClusters: function(e) {
		e.preventDefault()
		this.saveState()
		return this.validateField(e).done(() => {
			this.options.wizard.finish(this.field)
			this.trigger('done', this.field)
		})
	},

	submit: function(e) {
		e.preventDefault()
		this.saveState()
		return this.validateField(e).done(() => {
			this._submitSuccess(e)
		})
	},

	_submitSuccess: function(e) {
		// #38259 If operating under a wizard, the wizard is responsible for cloning
		// the field, and passing it in here, and we must share a copy of the field
		// with the wizard or the wizard's copy won't reflect the changes made.
		if (this.options.wizard) {
			this.options.field = _.cloneDeep(this.field)
			this.options.wizard.field = _.cloneDeep(this.field)
		}
		if (this.twoSteps()) {
			$.post({
				url: `api/datasets/${this.model.id}/clusters`,
				data: JSON.stringify({
					fieldIds: this.field.clusteredFieldIds
				}),
				success: resp => {
					let numClusters = this.$('[name=numClusters]').val()
					let matchingSummary = _.some(resp.summaries, s =>
						s.numClusters == numClusters)
					if (_.isEmpty(numClusters) || matchingSummary) {
						this.options.wizard.options.clusterResponse = resp
						this.options.wizard.next()
					} else {
						let msg = i18n.highlight(
							'datasets.field.clusterFields.error.numClusters',
							numClusters,
							'scorecards.field.numClusters',
							'scorecards.field.numClusters.auto',
							'scorecards.field.clusterAnalyze')
						this.formView.errorMessage(msg)
					}
				},
				error: this.formView.error.bind(this.formView)
			})
		} else {
			this.options.wizard.finish(this.field)
			this.trigger('done', this.field)
			this.formView.submit(e)
		}
	},

	twoSteps: function() {
		return this.field.fieldType === CLUSTER
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([...this.markedFields, this.wizardProgress,
			this.linkedDatasetSelector, this.equationButtonTooltip,
			this.columnDropdownTooltip, this.disabledButtonTooltip, this.dataTypeSelector,
			this.editableFieldChecker, this.defaultCalendarIdTooltip, this.dataTypeTooltip
			], 'remove')
	}
})

function renderTimeDisplayTypeSelect(elm, selectedFormat, addedOptions = []) {
	let baseOptions = _.map(enums.timeDisplayType, (code, value) => ({
		value,
		label: i18n.get(code),
		selected: value == selectedFormat
	}))
	const options = [...baseOptions, ...addedOptions]
	selects(elm, { options })
}

/**
 * Determines what the default field type should be when adding or editing a
 * field.  If the field type is not specified, it will be determined based on
 * the presence of a data source and/or column options.
 * @param fieldType the current field type, if it's an existing field
 * @param hasColumnOptions whether there are available options for creating
 * a basic field
 * @return {string} the default field type
 */
function getFieldType({ fieldType, hasColumnOptions }) {
	if (_.isNil(fieldType)) {
		if (hasColumnOptions) {
			fieldType = DATASET_FIELD_TYPE.BASIC
		} else {
			fieldType = DATASET_FIELD_TYPE.MANUAL
		}
	}
	return fieldType
}

_.extend(FieldForm, {
	renderTimeDisplayTypeSelect,
	getFieldType,
	fieldNameValidator
})
export default FieldForm
export { renderTimeDisplayTypeSelect }
export { getFieldType }
export { fieldNameValidator }

FieldForm.__filename__ = 'datasets/edit/fields/field-form.js'
